import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Scale, PauseCircle, NotebookPen, Sunrise } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useJourney } from "@/v2/V2Shell";
import { EnergyCheck } from "@/v2/EnergyCheck";
import { StepCard } from "@/v2/StepCard";
import { ContextCard } from "@/v2/ContextCard";
import { EveningWrap } from "@/v2/EveningWrap";
import { InstallTip } from "@/components/InstallTip";
import { BoundaryCard, YesterdayWrap, CarryCard } from "@/v2/DayCards";
import { FollowUpCard, PlansCard } from "@/v2/Plans";
import { EnvelopeCard, GoalPulse, RevealCard, extraLabel } from "@/v2/Pull";
import { MissionCard, ExperimentCard, MilestoneCard } from "@/v2/Growth";
import TodayV3 from "@/v2/today3/TodayV3";

const DT_LABEL = { build: "Build day", steady: "Steady day", recovery: "Recovery day", challenge: "Challenge day", discovery: "Discovery day", life: "Life-happens day" };

function WelcomeBack({ name, days, onEase, onDismiss }) {
  return (
    <div className="card p-6 bg-[#2D2926] border-[#2D2926] text-stone-100 rise" data-testid="welcome-back">
      <span className="eyebrow !text-stone-300 flex items-center gap-1.5"><Sunrise className="h-3.5 w-3.5" />Welcome back</span>
      <p className="font-display text-2xl mt-2 leading-snug">Good to see you, {name}. It's been {days} days, and nothing is lost.</p>
      <p className="text-sm text-stone-300 mt-2">How would you like to start?</p>
      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={onEase} data-testid="welcome-ease-btn" className="px-5 h-11 rounded-full bg-white text-stone-900 text-sm font-semibold">Ease back in (2-minute day)</button>
        <button onClick={onDismiss} data-testid="welcome-pickup-btn" className="px-5 h-11 rounded-full border border-white/30 text-sm font-semibold">Pick up where I left off</button>
      </div>
    </div>
  );
}

function WeightTargets({ day, weight, onDay }) {
  const t = day.targets || {};
  const set = async (patch) => { try { const { data } = await api.post("/v2/today/targets", patch); onDay(data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card p-5" data-testid="weight-targets">
      <div className="flex items-center justify-between mb-3">
        <span className="eyebrow flex items-center gap-1.5"><Scale className="h-3.5 w-3.5" />Weight Reset · today's targets</span>
        <Link to="/journey/plan/weight" data-testid="weight-targets-link" className="tap text-xs underline text-stone-600">Trend & review</Link>
      </div>
      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between gap-2"><span>Protein ({weight.plan.protein_g}g)</span>
          <div className="flex gap-1">{["hit", "close", "missed"].map((v) => <button key={v} onClick={() => set({ protein: v })} data-testid={`target-protein-${v}`} className={`chip px-3 py-2.5 rounded-full text-xs border capitalize ${t.protein === v ? "bg-stone-900 text-white border-stone-900" : "border-stone-200"}`}>{v}</button>)}</div></div>
        <div className="flex items-center justify-between gap-2"><span>Steps (goal {weight.plan.steps.toLocaleString()})</span>
          <input type="number" defaultValue={t.steps ?? ""} placeholder="0" onBlur={(e) => e.target.value !== "" && set({ steps: Number(e.target.value) })} data-testid="target-steps" className="w-24 h-9 px-3 rounded-lg border border-stone-200 text-right" /></div>
        <div className="flex items-center justify-between gap-2"><span>Strength session</span>
          <button onClick={() => set({ strength: !t.strength })} data-testid="target-strength" className={`chip px-3 py-2.5 rounded-full text-xs border ${t.strength ? "bg-[#6E8B74] text-white border-[#6E8B74]" : "border-stone-200"}`}>{t.strength ? "Done" : "Not yet"}</button></div>
      </div>
    </div>
  );
}

export default function V2Today() {
  const { user } = useAuth();
  const { s, reload } = useJourney();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [welcome, setWelcome] = useState(true);
  const first = (user.name || "").split(" ")[0];

  const load = () => api.get("/v2/today").then(({ data }) => setT(data));
  useEffect(() => { load(); }, []);
  const setDay = (day) => {
    setT((x) => ({ ...x, day }));
    if (["done", null].includes(day?.main?.status) || day?.extra?.status === "done") load();
  };

  const check = async (body) => {
    setBusy(true);
    try { await api.post("/v2/today/check", body); await load(); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const resume = async () => { await api.post("/v2/journey/status", { status: "active" }); reload(); toast.success("Welcome back. Your year clock is running again."); };
  const switchTo = async (v3) => { try { await api.put("/v2/today/version", { v3 }); setT(null); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!t) return null;
  const d = t.day;
  const r = s.readiness;
  const c = t.clock;
  const lateHour = c.hour >= 18 || c.hour < c.day_start || c.personal_day !== c.calendar_date;
  const evening = d && (lateHour || d.main?.status === "done" || !!d.wrap);
  return (
    <div className={`space-y-4 ${t.today_v3 ? "" : "max-w-2xl mx-auto"}`} data-testid="v2-today">
      {s.journey.status !== "active" && (
        <div className="card p-4 flex items-center gap-3 bg-[#F5F2EC]" data-testid="today-paused-banner">
          <PauseCircle className="h-5 w-5 text-stone-500" />
          <p className="text-sm flex-1">{s.journey.status === "paused" ? "Your journey is paused" : "Maintenance mode"}: 2-minute minimums only, and your year clock has stopped.</p>
          <button onClick={resume} data-testid="today-resume-btn" className="text-sm font-semibold underline">Resume</button>
        </div>
      )}
      {t.today_v3 ? <TodayV3 t={t} setT={setT} load={load} s={s} first={first} onClassic={t.today_v3_switch ? () => switchTo(false) : null}
        weightTargets={t.weight_active && t.weight && d ? <WeightTargets day={d} weight={t.weight} onDay={setDay} /> : null} /> : <>
      {t.today_v3_switch && <button onClick={() => switchTo(true)} data-testid="today-switch-v3" className="tap text-xs font-semibold text-[#C86D51] underline px-1">Switch to the new Today</button>}
      {t.yesterday_open && !t.boundary && <YesterdayWrap date={t.yesterday_date} onDone={load} />}
      {t.followup && !t.boundary && <FollowUpCard plan={t.followup} onDone={load}
        today={{ plan_id: d?.main?.plan_id || t.plan_step_pending?.plan_id, pending: !d, plan_text: d?.main?.plan_id ? d.main.text : t.plan_step_pending?.text, step_done: !!d?.main?.status }} />}
      {!d && t.plan_step_pending && !t.boundary && <p className="text-sm text-stone-600 px-1" data-testid="today-plan-pending">After your energy check, your step will be your plan: <b>{t.plan_step_pending.text}</b></p>}
      {t.boundary ? <BoundaryCard b={t.boundary} clock={c} onDone={load} /> : !d ? (
        <>
          {welcome && t.away_days >= 3 && !busy && <WelcomeBack name={first} days={t.away_days} onEase={() => check({ energy: "okay", sleep: null, circumstances: ["busy"] })} onDismiss={() => setWelcome(false)} />}
          {t.envelope && <EnvelopeCard env={t.envelope} onOpened={(env) => setT((x) => ({ ...x, envelope: env }))} />}
          <EnergyCheck name={first} hour={c.hour} onSubmit={check} busy={busy} />
        </>
      ) : (
        <>
          <div className="rise">
            <span data-testid="today-day-type" className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full border border-stone-300 text-stone-700 bg-white">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.phase.color }} />{d.shape_label || DT_LABEL[d.day_type]}</span>
            <p className="font-display text-xl sm:text-2xl leading-snug tracking-tight mt-4 text-stone-900" data-testid="today-note">{d.note}</p>
            {d.question && (
              <button onClick={() => nav("/talk", { state: { opener: d.question } })} data-testid="today-question-btn" className="mt-3 inline-flex items-center gap-2 text-sm text-stone-700 border-b border-stone-300 pb-0.5 hover:border-stone-800">
                <MessageCircle className="h-4 w-4" />{d.question}
              </button>
            )}
          </div>
          <MilestoneCard milestone={t.milestone} onSeen={load} />
          <GoalPulse pulse={t.pulse} onChange={load} />
          <MissionCard mission={t.mission} rescue={d.shape === "rescue"} />
          <RevealCard reveal={d.reveal} />
          {t.carry && <CarryCard step={t.carry} onDay={(day) => setT((x) => ({ ...x, day, carry: null }))} />}
          {d.main && <div className="rise d1"><StepCard step={d.main} slot="main" accent={s.phase.color} label={d.main.plan_id ? "Your step today · your plan" : d.main.carried_from ? "Your step today · kept from yesterday" : "Your step today"} onDay={setDay} /></div>}
          {extraLabel(d) && <div className="rise d1"><StepCard step={d.extra} slot="extra" compact accent={d.extra.kind === "challenge" ? "#B5642F" : "#6E8B74"} label={extraLabel(d)} onDay={setDay} /></div>}
          <ExperimentCard goal={t.pulse?.goal} featured={d.shape === "experiment"} />
          <InstallTip trigger={d.main?.status === "done" || d.extra?.status === "done"} />
          {t.weight_active && t.weight && <div className="rise d2"><WeightTargets day={d} weight={t.weight} onDay={setDay} /></div>}
          <div className="rise d2">{evening
            ? <EveningWrap day={d} tomorrow={t.tomorrow} onWrap={(x) => setT((o) => ({ ...o, day: x.day, tomorrow: x.tomorrow }))} />
            : <ContextCard s={s} d={extraLabel(d) ? { ...d, extra: null } : d} setDay={setDay} sunday={c.weekday_index === 6} />}</div>
          <PlansCard refresh={`${t.followup?.id}-${d.main?.id}-${d.main?.status}`} onToday={load} />
          <Link to="/today/log" data-testid="today-log-more" className="tap flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 px-1">
            <NotebookPen className="h-4 w-4" />Log more: mood, stress, symptoms, cycle, a note
          </Link>
        </>
      )}
      <Link to="/journey" data-testid="today-where-strip" className="card p-4 flex items-center gap-4 chip hover:border-stone-300">
        <span className="h-10 w-10 rounded-full grid place-items-center text-white font-display text-lg" style={{ background: s.phase.color }}>{s.phase.letter}</span>
        <span className="flex-1">
          <span className="block text-sm font-semibold">{s.phase.name} · week {s.week} of your year</span>
          <span className="block text-xs text-stone-600">{r.ready ? `You're ready for the next chapter. Tap to see.` : `${r.met} of 3 signs of readiness · showed up ${s.consistency_28} of the last 28 days`}</span>
        </span>
        <span className="flex gap-1">{r.markers.map((m) => <span key={m.text} className="h-2 w-2 rounded-full" style={{ background: m.met ? s.phase.color : "#E2DCD3" }} />)}</span>
      </Link>
      </>}
    </div>
  );
}
